import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import CountUp from "react-countup";
import { formatCurrency } from "../lib/formatCurrency";
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Plus, Search, Wallet, TrendingUp, MoreVertical, Edit, Trash2, 
  DollarSign, Banknote, Users, Paperclip, ClipboardList, Sparkles, Mail, Phone, Calculator
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { RecordAttachments } from '../components/RecordAttachments';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRANT_STATUSES = ['planning', 'active', 'suspended', 'completed', 'closed'];
const GRANT_TYPES = [
  { value: 'learnership', label: 'Learnership' },
  { value: 'skills_programme', label: 'Skills Programme' },
  { value: 'internship', label: 'Internship' },
  { value: 'apprenticeship', label: 'Apprenticeship' },
  { value: 'tvet_placement', label: 'TVET Placement' },
  { value: 'work_integrated_learning', label: 'Work Integrated Learning (WIL)' },
  { value: 'digital_skills_programme', label: 'Digital Skills Programme' },
  { value: 'community_development_project', label: 'Community Development Project' },
  { value: 'infrastructure_project', label: 'Infrastructure Project' },
  { value: 'enterprise_development', label: 'Enterprise Development' },
  { value: 'other', label: 'Other' },
];
const DISB_STATUSES = ['pending', 'partial', 'received', 'overdue'];
const DELIVERABLE_STATUSES = ['not_started', 'in_progress', 'completed', 'overdue'];

const DELIVERABLE_TEMPLATES = {
  1: ['Project Planning', 'Recruitment', 'Venue Procurement', 'Printing of Learning Material', 'Staff Appointments'],
  2: ['Training Delivery', 'Learner Support', 'Assessments'],
  3: ['Moderation', 'Workplace Monitoring', 'Site Visits'],
  4: ['Verification', 'Certification'],
  5: ['Close-out Report', 'Financial Reconciliation', 'Final Audit'],
};

const statusColors = {
  planning: 'bg-blue-100 text-blue-700',
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-amber-100 text-amber-700',
  completed: 'bg-slate-100 text-slate-700',
  closed: 'bg-slate-200 text-slate-600',
  // legacy statuses on old records
  applied: 'bg-blue-100 text-blue-700',
  under_review: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
};

const disbStatusColors = {
  pending: 'bg-amber-100 text-amber-700',
  partial: 'bg-blue-100 text-blue-700',
  received: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-rose-100 text-rose-700',
};

const deliverableStatusColors = {
  not_started: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-rose-100 text-rose-700',
};

const OFFICIAL_ROLES = [
  { key: 'project_manager', label: 'Project Manager' },
  { key: 'programme_coordinator', label: 'Programme Coordinator' },
  { key: 'finance_officer', label: 'Finance Officer' },
  { key: 'operations_manager', label: 'Operations Manager' },
  { key: 'training_manager', label: 'Training Manager' },
  { key: 'assessment_manager', label: 'Assessment Manager' },
  { key: 'document_controller', label: 'Document Controller' },
  { key: 'compliance_officer', label: 'Compliance Officer' },
  { key: 'printing_coordinator', label: 'Printing Coordinator' },
];
const roleLabel = (key) => OFFICIAL_ROLES.find(r => r.key === key)?.label || key;

const BUDGET_CATEGORIES = [
  { key: 'personnel', label: 'Personnel', subs: [['salaries', 'Salaries'], ['facilitators', 'Facilitators'], ['assessors', 'Assessors'], ['moderators', 'Moderators'], ['coordinators', 'Coordinators']] },
  { key: 'travel', label: 'Travel', subs: [['accommodation', 'Accommodation'], ['flights', 'Flights'], ['fuel', 'Fuel'], ['vehicle_hire', 'Vehicle Hire'], ['toll_gates', 'Toll Gates'], ['parking', 'Parking']] },
  { key: 'training', label: 'Training', subs: [['venues', 'Venues'], ['catering', 'Catering'], ['printing', 'Printing'], ['stationery', 'Stationery'], ['learner_packs', 'Learner Packs']] },
  { key: 'operations', label: 'Operations', subs: [['internet', 'Internet'], ['office_rental', 'Office Rental'], ['utilities', 'Utilities'], ['software', 'Software'], ['marketing', 'Marketing']] },
  { key: 'compliance', label: 'Compliance', subs: [['audits', 'Audits'], ['accreditation', 'Accreditation'], ['insurance', 'Insurance']] },
  { key: 'assets', label: 'Assets', subs: [['computers', 'Computers'], ['printers', 'Printers'], ['furniture', 'Furniture'], ['equipment', 'Equipment']] },
  { key: 'miscellaneous', label: 'Miscellaneous', subs: [['contingency', 'Contingency'], ['other_costs', 'Other Costs']] },
];


const EMPTY_FORM = {
  name: '', grant_number: '', funder: '', funding_organisation: '',
  officials: {},
  status: 'planning', grant_type: 'none', project_id: 'none',
  amount: 0, start_date: '', end_date: '', description: '', requirements: ''
};

export const GrantsPage = () => {
  const [grants, setGrants] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [editingGrant, setEditingGrant] = useState(null);
  const [grantForm, setGrantForm] = useState(EMPTY_FORM);

  // Disbursements dialog
  const [showDisbDialog, setShowDisbDialog] = useState(false);
  const [disbGrant, setDisbGrant] = useState(null);
  const [disbRows, setDisbRows] = useState([]);
  const [savingDisb, setSavingDisb] = useState(false);

  // Officials dialog (Module 4)
  const [showOfficialsDialog, setShowOfficialsDialog] = useState(false);
  const [officialsGrant, setOfficialsGrant] = useState(null);
  const [officialsData, setOfficialsData] = useState([]);
  const [loadingOfficials, setLoadingOfficials] = useState(false);

  // Budget dialog (Module 5)
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);
  const [budgetGrant, setBudgetGrant] = useState(null);
  const [budgetRows, setBudgetRows] = useState([]);
  const [savingBudget, setSavingBudget] = useState(false);

  const openBudgetDialog = (grant) => {
    setBudgetGrant(grant);
    setBudgetRows((grant.budget_items || []).map(b => ({ ...b })));
    setShowBudgetDialog(true);
  };

  const addBudgetRow = (category) => {
    const cat = BUDGET_CATEGORIES.find(c => c.key === category);
    setBudgetRows(rows => [...rows, {
      id: null, category, subcategory: cat?.subs[0]?.[0] || null,
      description: '', budgeted_amount: 0, spent_amount: 0, notes: ''
    }]);
  };

  const updateBudgetRow = (idx, patch) => {
    setBudgetRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const removeBudgetRow = (idx) => {
    setBudgetRows(rows => rows.filter((_, i) => i !== idx));
  };

  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      const payload = budgetRows.map(r => ({
        ...r,
        description: r.description || null,
        notes: r.notes || null,
        budgeted_amount: Number(r.budgeted_amount) || 0,
        spent_amount: Number(r.spent_amount) || 0,
      }));
      const res = await axios.put(`${API}/grants/${budgetGrant.id}/budget`, payload);
      setBudgetGrant(res.data);
      setBudgetRows((res.data.budget_items || []).map(b => ({ ...b })));
      toast.success('Budget saved');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save budget');
    } finally {
      setSavingBudget(false);
    }
  };

  const budgetTotal = budgetRows.reduce((s, r) => s + (Number(r.budgeted_amount) || 0), 0);
  const budgetSpent = budgetRows.reduce((s, r) => s + (Number(r.spent_amount) || 0), 0);
  const budgetRemaining = budgetTotal - budgetSpent;
  const budgetPct = budgetTotal ? Math.round((budgetSpent / budgetTotal) * 1000) / 10 : 0;

  const openOfficialsDialog = async (grant) => {
    setOfficialsGrant(grant);
    setOfficialsData([]);
    setShowOfficialsDialog(true);
    setLoadingOfficials(true);
    try {
      const res = await axios.get(`${API}/grants/${grant.id}/officials`);
      setOfficialsData(res.data);
    } catch (error) {
      toast.error('Failed to load officials');
    } finally {
      setLoadingOfficials(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [grantsRes, projectsRes, usersRes] = await Promise.all([
        axios.get(`${API}/grants`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/users`).catch(() => ({ data: [] }))
      ]);
      setGrants(grantsRes.data);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load grants');
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => ({
    ...grantForm,
    grant_number: grantForm.grant_number || null,
    funder: grantForm.funder || null,
    funding_organisation: grantForm.funding_organisation || null,
    officials: OFFICIAL_ROLES
      .map(r => {
        const off = grantForm.officials[r.key] || {};
        const uid = off.user_id && off.user_id !== 'none' ? off.user_id : null;
        return { role: r.key, user_id: uid, responsibilities: off.responsibilities || null };
      })
      .filter(o => o.user_id || o.responsibilities),
    grant_type: grantForm.grant_type === 'none' ? null : grantForm.grant_type,
    project_id: grantForm.project_id === 'none' ? null : grantForm.project_id,
    amount: Number(grantForm.amount) || 0,
    start_date: grantForm.start_date || null,
    end_date: grantForm.end_date || null,
    description: grantForm.description || null,
    requirements: grantForm.requirements || null,
  });

  const handleSaveGrant = async () => {
    if (!grantForm.name) { toast.error('Grant name is required'); return; }
    if (!grantForm.funder) { toast.error('Funder is required'); return; }
    try {
      if (editingGrant) {
        await axios.put(`${API}/grants/${editingGrant.id}`, buildPayload());
        toast.success('Grant updated');
      } else {
        await axios.post(`${API}/grants`, buildPayload());
        toast.success('Grant created');
      }
      setShowGrantDialog(false);
      setEditingGrant(null);
      setGrantForm(EMPTY_FORM);
      loadData();
    } catch (error) {
      toast.error('Failed to save grant');
    }
  };

  const handleDeleteGrant = async (grantId) => {
    if (!window.confirm('Delete this grant?')) return;
    try {
      await axios.delete(`${API}/grants/${grantId}`);
      toast.success('Grant deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete grant');
    }
  };

  const openEditDialog = (grant) => {
    setEditingGrant(grant);
    const officials = {};
    (grant.officials || []).forEach(o => {
      officials[o.role] = { user_id: o.user_id || 'none', responsibilities: o.responsibilities || '' };
    });
    if (!(grant.officials || []).length) {
      // legacy records: map the old 4 role fields
      const legacy = {
        project_manager: grant.project_manager_id,
        programme_coordinator: grant.programme_coordinator_id,
        finance_officer: grant.finance_officer_id,
        operations_manager: grant.operations_manager_id,
      };
      Object.entries(legacy).forEach(([role, uid]) => {
        if (uid) officials[role] = { user_id: uid, responsibilities: '' };
      });
    }
    setGrantForm({
      name: grant.name || '',
      grant_number: grant.grant_number || '',
      funder: grant.funder || grant.donor || '',
      funding_organisation: grant.funding_organisation || '',
      officials,
      status: GRANT_STATUSES.includes(grant.status) ? grant.status : 'planning',
      grant_type: grant.grant_type || 'none',
      project_id: grant.project_id || 'none',
      amount: grant.amount || 0,
      start_date: grant.start_date?.split('T')[0] || '',
      end_date: grant.end_date?.split('T')[0] || '',
      description: grant.description || '',
      requirements: grant.requirements || ''
    });
    setShowGrantDialog(true);
  };

  // ----- Disbursements -----
  const openDisbDialog = (grant) => {
    setDisbGrant(grant);
    setDisbRows((grant.disbursements || []).map(d => ({ ...d })));
    setShowDisbDialog(true);
  };

  const addDisbRow = () => {
    setDisbRows(rows => {
      if (rows.length >= 5) { toast.error('Maximum of 5 disbursements per grant'); return rows; }
      return [...rows, {
        id: null,
        disbursement_number: rows.length + 1,
        planned_date: '', actual_date_received: '',
        amount_expected: 0, amount_received: 0,
        status: 'pending', payment_notes: '', _new: true
      }];
    });
  };

  const updateDisbRow = (idx, patch) => {
    setDisbRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const removeDisbRow = (idx) => {
    setDisbRows(rows => rows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, disbursement_number: i + 1 })));
  };

  // ----- Deliverables (Module 3) -----
  const EMPTY_DELIVERABLE = { id: null, name: '', responsible_person_id: null, due_date: '', status: 'not_started', percentage_complete: 0, comments: '', _new: true };

  const addDeliverable = (disbIdx, name = '') => {
    setDisbRows(rows => rows.map((r, i) => i === disbIdx
      ? { ...r, deliverables: [...(r.deliverables || []), { ...EMPTY_DELIVERABLE, name }] }
      : r));
  };

  const addTemplateDeliverables = (disbIdx) => {
    const templates = DELIVERABLE_TEMPLATES[disbIdx + 1] || [];
    setDisbRows(rows => rows.map((r, i) => {
      if (i !== disbIdx) return r;
      const existing = (r.deliverables || []).map(d => d.name);
      const toAdd = templates.filter(t => !existing.includes(t)).map(name => ({ ...EMPTY_DELIVERABLE, name }));
      return { ...r, deliverables: [...(r.deliverables || []), ...toAdd] };
    }));
  };

  const updateDeliverable = (disbIdx, delIdx, patch) => {
    setDisbRows(rows => rows.map((r, i) => i === disbIdx
      ? { ...r, deliverables: r.deliverables.map((d, j) => j === delIdx ? { ...d, ...patch } : d) }
      : r));
  };

  const removeDeliverable = (disbIdx, delIdx) => {
    setDisbRows(rows => rows.map((r, i) => i === disbIdx
      ? { ...r, deliverables: r.deliverables.filter((_, j) => j !== delIdx) }
      : r));
  };

  const saveDisbursements = async () => {
    setSavingDisb(true);
    try {
      const payload = disbRows.map(({ _new, outstanding_balance, ...d }) => ({
        ...d,
        planned_date: d.planned_date || null,
        actual_date_received: d.actual_date_received || null,
        amount_expected: Number(d.amount_expected) || 0,
        amount_received: Number(d.amount_received) || 0,
        payment_notes: d.payment_notes || null,
        deliverables: (d.deliverables || []).filter(dl => dl.name?.trim()).map(({ _new: dn, ...dl }) => ({
          ...dl,
          responsible_person_id: dl.responsible_person_id === 'none' ? null : dl.responsible_person_id,
          due_date: dl.due_date || null,
          percentage_complete: Number(dl.percentage_complete) || 0,
          comments: dl.comments || null,
        })),
      }));
      const res = await axios.put(`${API}/grants/${disbGrant.id}/disbursements`, payload);
      setDisbGrant(res.data);
      setDisbRows((res.data.disbursements || []).map(d => ({ ...d })));
      toast.success('Disbursements saved');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save disbursements');
    } finally {
      setSavingDisb(false);
    }
  };

  // Live totals inside the disbursements dialog
  const liveTotalValue = disbRows.length
    ? disbRows.reduce((s, d) => s + (Number(d.amount_expected) || 0), 0)
    : (disbGrant?.amount || 0);
  const liveTotalReceived = disbRows.length
    ? disbRows.reduce((s, d) => s + (Number(d.amount_received) || 0), 0)
    : (disbGrant?.amount_received || 0);
  const liveOutstanding = liveTotalValue - liveTotalReceived;
  const livePct = liveTotalValue ? Math.round((liveTotalReceived / liveTotalValue) * 1000) / 10 : 0;

  const filteredGrants = grants.filter(grant => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = grant.name?.toLowerCase().includes(q) ||
      grant.funder?.toLowerCase().includes(q) ||
      grant.grant_number?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || grant.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalGrants = grants.length;
  const totalAmount = grants.reduce((sum, g) => sum + (g.total_grant_value || 0), 0);
  const totalReceived = grants.reduce((sum, g) => sum + (g.total_received || 0), 0);
  const activeGrants = grants.filter(g => g.status === 'active').length;

  const getProjectName = (projectId) => projects.find(p => p.id === projectId)?.name || '-';
  const getUserName = (userId) => users.find(u => u.id === userId)?.name || '-';
  const typeLabel = (v) => GRANT_TYPES.find(t => t.value === v)?.label || '-';

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading grants...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="grants-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Grant Management</h1>
          <p className="text-muted-foreground">Grants, staff assignments & disbursement tracking</p>
        </div>
        <Button data-testid="new-grant-btn" onClick={() => { setEditingGrant(null); setGrantForm(EMPTY_FORM); setShowGrantDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" /> New Grant
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-black text-[#D4E100] flex items-center justify-center shrink-0">
              <Wallet className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold"><CountUp end={totalGrants} duration={1} /></p>
              <p className="text-sm text-muted-foreground">Total Grants</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-green-100 text-green-600 flex items-center justify-center shrink-0">
              <TrendingUp className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold"><CountUp end={activeGrants} duration={1} /></p>
              <p className="text-sm text-muted-foreground">Active</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
              <DollarSign className="h-6 w-6" />
            </div>
            <div>
              <p className="text-lg font-bold">R <CountUp end={totalAmount} duration={1.2} separator=" " /></p>
              <p className="text-sm text-muted-foreground">Total Value</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
              <Banknote className="h-6 w-6" />
            </div>
            <div>
              <p className="text-lg font-bold">R <CountUp end={totalReceived} duration={1.2} separator=" " /></p>
              <p className="text-sm text-muted-foreground">Received</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search grants, numbers, funders..." 
            className="pl-10" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="grants-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="grants-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {GRANT_STATUSES.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Grants Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Grant</TableHead>
                <TableHead>Funder</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Project Manager</TableHead>
                <TableHead className="text-right">Total Value</TableHead>
                <TableHead className="text-right">Received</TableHead>
                <TableHead>% Received</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGrants.map(grant => (
                <TableRow key={grant.id} data-testid={`grant-row-${grant.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{grant.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {grant.grant_number ? `#${grant.grant_number}` : ''}
                        {grant.grant_number && (grant.disbursements?.length > 0) ? ' • ' : ''}
                        {grant.disbursements?.length > 0 ? `${grant.disbursements.length} disbursement${grant.disbursements.length > 1 ? 's' : ''}` : ''}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{grant.funder || grant.donor || '-'}</TableCell>
                  <TableCell className="text-sm">{typeLabel(grant.grant_type)}</TableCell>
                  <TableCell className="text-sm">{getUserName(grant.project_manager_id)}</TableCell>
                  <TableCell className="text-sm font-mono text-right">{formatCurrency(grant.total_grant_value)}</TableCell>
                  <TableCell className="text-sm font-mono text-right">{formatCurrency(grant.total_received)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={grant.percentage_received || 0} className="h-2 w-16" />
                      <span className="text-xs text-muted-foreground">{grant.percentage_received || 0}%</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[grant.status] || 'bg-slate-100 text-slate-700'}>{grant.status?.replace('_', ' ')}</Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`grant-menu-${grant.id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(grant)} data-testid={`grant-edit-${grant.id}`}>
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openDisbDialog(grant)} data-testid={`grant-disb-${grant.id}`}>
                          <Banknote className="h-4 w-4 mr-2" /> Disbursements
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openOfficialsDialog(grant)} data-testid={`grant-officials-${grant.id}`}>
                          <Users className="h-4 w-4 mr-2" /> Officials
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openBudgetDialog(grant)} data-testid={`grant-budget-${grant.id}`}>
                          <Calculator className="h-4 w-4 mr-2" /> Budget
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDeleteGrant(grant.id)} className="text-red-600" data-testid={`grant-delete-${grant.id}`}>
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredGrants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No grants found. Create one to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Grant Dialog */}
      <Dialog open={showGrantDialog} onOpenChange={setShowGrantDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="grant-dialog">
          <DialogHeader>
            <DialogTitle>{editingGrant ? 'Edit Grant' : 'Create New Grant'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Grant Information</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Grant Name *</Label>
                  <Input 
                    value={grantForm.name}
                    onChange={(e) => setGrantForm({ ...grantForm, name: e.target.value })}
                    placeholder="Enter grant name"
                    data-testid="grant-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Grant Number</Label>
                  <Input 
                    value={grantForm.grant_number}
                    onChange={(e) => setGrantForm({ ...grantForm, grant_number: e.target.value })}
                    placeholder="e.g. GRT-2026-001"
                    data-testid="grant-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Funder *</Label>
                  <Input 
                    value={grantForm.funder}
                    onChange={(e) => setGrantForm({ ...grantForm, funder: e.target.value })}
                    placeholder="Funder"
                    data-testid="grant-funder-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Funding Organisation</Label>
                  <Input 
                    value={grantForm.funding_organisation}
                    onChange={(e) => setGrantForm({ ...grantForm, funding_organisation: e.target.value })}
                    placeholder="Funding organisation"
                    data-testid="grant-funding-org-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Grant Type</Label>
                  <Select value={grantForm.grant_type} onValueChange={(v) => setGrantForm({ ...grantForm, grant_type: v })}>
                    <SelectTrigger data-testid="grant-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {GRANT_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Grant Status</Label>
                  <Select value={grantForm.status} onValueChange={(v) => setGrantForm({ ...grantForm, status: v })}>
                    <SelectTrigger data-testid="grant-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GRANT_STATUSES.map(s => (
                        <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Linked Project</Label>
                  <Select value={grantForm.project_id} onValueChange={(v) => setGrantForm({ ...grantForm, project_id: v })}>
                    <SelectTrigger data-testid="grant-project-select">
                      <SelectValue placeholder="Link to project" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Project</SelectItem>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Grant Amount (R)</Label>
                  <Input
                    type="number"
                    value={grantForm.amount}
                    onChange={(e) => setGrantForm({ ...grantForm, amount: Number(e.target.value) || 0 })}
                    placeholder="0"
                    data-testid="grant-amount-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                <Users className="h-4 w-4" /> Responsible Officials
              </p>
              <div className="space-y-2">
                {OFFICIAL_ROLES.map(role => {
                  const off = grantForm.officials[role.key] || { user_id: 'none', responsibilities: '' };
                  const setOff = (patch) => setGrantForm({
                    ...grantForm,
                    officials: { ...grantForm.officials, [role.key]: { ...off, ...patch } }
                  });
                  return (
                    <div key={role.key} className="grid grid-cols-1 sm:grid-cols-2 gap-2 border rounded-md p-3">
                      <div className="space-y-1">
                        <Label className="text-xs font-semibold">{role.label}</Label>
                        <Select value={off.user_id || 'none'} onValueChange={(v) => setOff({ user_id: v })}>
                          <SelectTrigger data-testid={`grant-${role.key}-select`}>
                            <SelectValue placeholder={`Select ${role.label.toLowerCase()}`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Not assigned</SelectItem>
                            {users.map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Responsibilities</Label>
                        <Input
                          value={off.responsibilities || ''}
                          onChange={(e) => setOff({ responsibilities: e.target.value })}
                          placeholder="Key responsibilities"
                          data-testid={`grant-${role.key}-responsibilities`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input 
                  type="date"
                  value={grantForm.start_date}
                  onChange={(e) => setGrantForm({ ...grantForm, start_date: e.target.value })}
                  data-testid="grant-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input 
                  type="date"
                  value={grantForm.end_date}
                  onChange={(e) => setGrantForm({ ...grantForm, end_date: e.target.value })}
                  data-testid="grant-end-date"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={grantForm.description}
                onChange={(e) => setGrantForm({ ...grantForm, description: e.target.value })}
                placeholder="Grant description"
                rows={2}
                data-testid="grant-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Requirements</Label>
              <Textarea 
                value={grantForm.requirements}
                onChange={(e) => setGrantForm({ ...grantForm, requirements: e.target.value })}
                placeholder="Grant requirements and conditions"
                rows={2}
                data-testid="grant-requirements-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGrantDialog(false)} data-testid="grant-cancel-btn">Cancel</Button>
            <Button onClick={handleSaveGrant} data-testid="grant-save-btn">{editingGrant ? 'Update Grant' : 'Create Grant'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disbursements Dialog */}
      <Dialog open={showDisbDialog} onOpenChange={setShowDisbDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="disbursements-dialog">
          <DialogHeader>
            <DialogTitle>Disbursements — {disbGrant?.name}</DialogTitle>
          </DialogHeader>

          {/* Auto-calculated summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="disb-summary">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Total Grant Value</p>
              <p className="font-bold font-mono text-sm" data-testid="disb-total-value">{formatCurrency(liveTotalValue)}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Total Received</p>
              <p className="font-bold font-mono text-sm text-emerald-600" data-testid="disb-total-received">{formatCurrency(liveTotalReceived)}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Outstanding Balance</p>
              <p className="font-bold font-mono text-sm text-amber-600" data-testid="disb-outstanding">{formatCurrency(liveOutstanding)}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">% Received</p>
              <div className="flex items-center gap-2">
                <Progress value={livePct} className="h-2 flex-1" />
                <span className="text-sm font-bold" data-testid="disb-percentage">{livePct}%</span>
              </div>
            </div>
          </div>

          <div className="space-y-4 py-2">
            {disbRows.map((row, idx) => {
              const outstanding = (Number(row.amount_expected) || 0) - (Number(row.amount_received) || 0);
              return (
                <div key={row.id || `new-${idx}`} className="rounded-lg border p-4 space-y-3" data-testid={`disb-row-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono">Disbursement #{idx + 1}</Badge>
                      <Badge className={disbStatusColors[row.status] || 'bg-slate-100 text-slate-700'}>{row.status}</Badge>
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeDisbRow(idx)} data-testid={`disb-remove-${idx}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Planned Date</Label>
                      <Input type="date" value={row.planned_date || ''} onChange={(e) => updateDisbRow(idx, { planned_date: e.target.value })} data-testid={`disb-planned-${idx}`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Actual Date Received</Label>
                      <Input type="date" value={row.actual_date_received || ''} onChange={(e) => updateDisbRow(idx, { actual_date_received: e.target.value })} data-testid={`disb-actual-${idx}`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Amount Expected (R)</Label>
                      <Input type="number" value={row.amount_expected} onChange={(e) => updateDisbRow(idx, { amount_expected: e.target.value })} data-testid={`disb-expected-${idx}`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Amount Received (R)</Label>
                      <Input type="number" value={row.amount_received} onChange={(e) => updateDisbRow(idx, { amount_received: e.target.value })} data-testid={`disb-received-${idx}`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Status</Label>
                      <Select value={row.status} onValueChange={(v) => updateDisbRow(idx, { status: v })}>
                        <SelectTrigger data-testid={`disb-status-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DISB_STATUSES.map(s => (
                            <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Outstanding Balance</Label>
                      <div className={`h-10 flex items-center px-3 rounded-md border bg-muted/40 font-mono text-sm ${outstanding > 0 ? 'text-amber-600' : 'text-emerald-600'}`} data-testid={`disb-row-outstanding-${idx}`}>
                        {formatCurrency(outstanding)}
                      </div>
                    </div>
                    <div className="space-y-1 col-span-2">
                      <Label className="text-xs">Payment Notes</Label>
                      <Input value={row.payment_notes || ''} onChange={(e) => updateDisbRow(idx, { payment_notes: e.target.value })} placeholder="Notes about this payment" data-testid={`disb-notes-${idx}`} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1 mb-1"><Paperclip className="h-3 w-3" /> Supporting Documents</Label>
                    {row.id && !row._new ? (
                      <RecordAttachments parentType="grant" parentId={row.id} compact />
                    ) : (
                      <p className="text-xs text-muted-foreground italic">Save disbursements first to attach documents.</p>
                    )}
                  </div>

                  {/* Deliverables (Module 3) */}
                  <div className="border-t pt-3 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-sm font-semibold flex items-center gap-2">
                        <ClipboardList className="h-4 w-4" /> Deliverables ({(row.deliverables || []).length})
                      </p>
                      <div className="flex gap-2">
                        {(DELIVERABLE_TEMPLATES[idx + 1] || []).length > 0 && (
                          <Button variant="outline" size="sm" onClick={() => addTemplateDeliverables(idx)} data-testid={`disb-template-btn-${idx}`}>
                            <Sparkles className="h-3.5 w-3.5 mr-1" /> Use Template
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => addDeliverable(idx)} data-testid={`disb-add-deliverable-${idx}`}>
                          <Plus className="h-3.5 w-3.5 mr-1" /> Add Deliverable
                        </Button>
                      </div>
                    </div>

                    {(row.deliverables || []).map((dl, dIdx) => (
                      <div key={dl.id || `new-dl-${dIdx}`} className="rounded-md border bg-muted/20 p-3 space-y-2" data-testid={`deliverable-${idx}-${dIdx}`}>
                        <div className="flex items-center gap-2">
                          <Input
                            value={dl.name}
                            onChange={(e) => updateDeliverable(idx, dIdx, { name: e.target.value })}
                            placeholder="Deliverable name"
                            className="flex-1 font-medium"
                            data-testid={`deliverable-name-${idx}-${dIdx}`}
                          />
                          <Badge className={deliverableStatusColors[dl.status] || 'bg-slate-100 text-slate-700'}>{dl.status?.replace('_', ' ')}</Badge>
                          <Button variant="ghost" size="sm" className="text-red-600 shrink-0" onClick={() => removeDeliverable(idx, dIdx)} data-testid={`deliverable-remove-${idx}-${dIdx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs">Responsible Person</Label>
                            <Select value={dl.responsible_person_id || 'none'} onValueChange={(v) => updateDeliverable(idx, dIdx, { responsible_person_id: v })}>
                              <SelectTrigger data-testid={`deliverable-responsible-${idx}-${dIdx}`}>
                                <SelectValue placeholder="Assign" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Not assigned</SelectItem>
                                {users.map(u => (
                                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Due Date</Label>
                            <Input type="date" value={dl.due_date || ''} onChange={(e) => updateDeliverable(idx, dIdx, { due_date: e.target.value })} data-testid={`deliverable-due-${idx}-${dIdx}`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Status</Label>
                            <Select value={dl.status} onValueChange={(v) => updateDeliverable(idx, dIdx, { status: v })}>
                              <SelectTrigger data-testid={`deliverable-status-${idx}-${dIdx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {DELIVERABLE_STATUSES.map(s => (
                                  <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">% Complete</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number" min="0" max="100"
                                value={dl.percentage_complete}
                                onChange={(e) => updateDeliverable(idx, dIdx, { percentage_complete: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                                className="w-20"
                                data-testid={`deliverable-pct-${idx}-${dIdx}`}
                              />
                              <Progress value={Number(dl.percentage_complete) || 0} className="h-2 flex-1" />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Comments</Label>
                          <Input value={dl.comments || ''} onChange={(e) => updateDeliverable(idx, dIdx, { comments: e.target.value })} placeholder="Comments" data-testid={`deliverable-comments-${idx}-${dIdx}`} />
                        </div>
                        <div>
                          <Label className="text-xs flex items-center gap-1 mb-1"><Paperclip className="h-3 w-3" /> Supporting Documents</Label>
                          {dl.id && !dl._new ? (
                            <RecordAttachments parentType="grant" parentId={dl.id} compact />
                          ) : (
                            <p className="text-xs text-muted-foreground italic">Save disbursements first to attach documents.</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {disbRows.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-6">No disbursements yet. Add up to 5 disbursements for this grant.</p>
            )}

            <Button variant="outline" onClick={addDisbRow} disabled={disbRows.length >= 5} className="w-full" data-testid="disb-add-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Disbursement {disbRows.length >= 5 ? '(max 5 reached)' : `(${disbRows.length}/5)`}
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisbDialog(false)} data-testid="disb-close-btn">Close</Button>
            <Button onClick={saveDisbursements} disabled={savingDisb} data-testid="disb-save-btn">
              {savingDisb ? 'Saving...' : 'Save Disbursements'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Budget Dialog (Module 5) */}
      <Dialog open={showBudgetDialog} onOpenChange={setShowBudgetDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="budget-dialog">
          <DialogHeader>
            <DialogTitle>Budget — {budgetGrant?.name}</DialogTitle>
          </DialogHeader>

          {/* Auto-calculated summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="budget-summary">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Total Budgeted</p>
              <p className="font-bold font-mono text-sm" data-testid="budget-total">{formatCurrency(budgetTotal)}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Total Spent</p>
              <p className="font-bold font-mono text-sm text-blue-600" data-testid="budget-spent">{formatCurrency(budgetSpent)}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Remaining</p>
              <p className={`font-bold font-mono text-sm ${budgetRemaining < 0 ? 'text-rose-600' : 'text-emerald-600'}`} data-testid="budget-remaining">{formatCurrency(budgetRemaining)}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">% Utilised</p>
              <div className="flex items-center gap-2">
                <Progress value={Math.min(100, budgetPct)} className="h-2 flex-1" />
                <span className="text-sm font-bold" data-testid="budget-pct">{budgetPct}%</span>
              </div>
            </div>
          </div>
          {budgetGrant?.total_grant_value > 0 && budgetTotal > budgetGrant.total_grant_value && (
            <p className="text-xs text-amber-600 font-medium" data-testid="budget-over-warning">
              ⚠ Budget ({formatCurrency(budgetTotal)}) exceeds the total grant value ({formatCurrency(budgetGrant.total_grant_value)}).
            </p>
          )}

          <div className="space-y-4 py-2">
            {BUDGET_CATEGORIES.map(cat => {
              const catRows = budgetRows.map((r, idx) => ({ ...r, _idx: idx })).filter(r => r.category === cat.key);
              const catBudgeted = catRows.reduce((s, r) => s + (Number(r.budgeted_amount) || 0), 0);
              const catSpent = catRows.reduce((s, r) => s + (Number(r.spent_amount) || 0), 0);
              return (
                <div key={cat.key} className="rounded-lg border" data-testid={`budget-cat-${cat.key}`}>
                  <div className="flex items-center justify-between px-4 py-2.5 bg-muted/40 rounded-t-lg flex-wrap gap-2">
                    <p className="font-semibold text-sm">{cat.label}</p>
                    <div className="flex items-center gap-3">
                      {catRows.length > 0 && (
                        <span className="text-xs font-mono text-muted-foreground">
                          {formatCurrency(catSpent)} / {formatCurrency(catBudgeted)}
                        </span>
                      )}
                      <Button variant="outline" size="sm" onClick={() => addBudgetRow(cat.key)} data-testid={`budget-add-${cat.key}`}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add
                      </Button>
                    </div>
                  </div>
                  {catRows.length > 0 && (
                    <div className="p-3 space-y-2">
                      {catRows.map(r => {
                        const remaining = (Number(r.budgeted_amount) || 0) - (Number(r.spent_amount) || 0);
                        return (
                          <div key={r.id || `new-${r._idx}`} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end border rounded-md p-2.5" data-testid={`budget-row-${r._idx}`}>
                            <div className="space-y-1">
                              <Label className="text-xs">Item</Label>
                              <Select value={r.subcategory || ''} onValueChange={(v) => updateBudgetRow(r._idx, { subcategory: v })}>
                                <SelectTrigger data-testid={`budget-sub-${r._idx}`}>
                                  <SelectValue placeholder="Item" />
                                </SelectTrigger>
                                <SelectContent>
                                  {cat.subs.map(([k, l]) => (
                                    <SelectItem key={k} value={k}>{l}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Description</Label>
                              <Input value={r.description || ''} onChange={(e) => updateBudgetRow(r._idx, { description: e.target.value })} placeholder="Detail" data-testid={`budget-desc-${r._idx}`} />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Budgeted (R)</Label>
                              <Input type="number" value={r.budgeted_amount} onChange={(e) => updateBudgetRow(r._idx, { budgeted_amount: e.target.value })} data-testid={`budget-amount-${r._idx}`} />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Spent (R)</Label>
                              <Input type="number" value={r.spent_amount} onChange={(e) => updateBudgetRow(r._idx, { spent_amount: e.target.value })} data-testid={`budget-spent-${r._idx}`} />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Remaining</Label>
                              <div className={`h-10 flex items-center px-3 rounded-md border bg-muted/40 font-mono text-xs ${remaining < 0 ? 'text-rose-600' : 'text-emerald-600'}`} data-testid={`budget-row-remaining-${r._idx}`}>
                                {formatCurrency(remaining)}
                              </div>
                            </div>
                            <div className="flex items-end gap-1">
                              <div className="space-y-1 flex-1">
                                <Label className="text-xs">Notes</Label>
                                <Input value={r.notes || ''} onChange={(e) => updateBudgetRow(r._idx, { notes: e.target.value })} placeholder="Notes" data-testid={`budget-notes-${r._idx}`} />
                              </div>
                              <Button variant="ghost" size="sm" className="text-red-600 shrink-0" onClick={() => removeBudgetRow(r._idx)} data-testid={`budget-remove-${r._idx}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBudgetDialog(false)} data-testid="budget-close-btn">Close</Button>
            <Button onClick={saveBudget} disabled={savingBudget} data-testid="budget-save-btn">
              {savingBudget ? 'Saving...' : 'Save Budget'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Officials Dialog (Module 4) */}
      <Dialog open={showOfficialsDialog} onOpenChange={setShowOfficialsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="officials-dialog">
          <DialogHeader>
            <DialogTitle>Responsible Officials — {officialsGrant?.name}</DialogTitle>
          </DialogHeader>
          {loadingOfficials ? (
            <div className="py-10 text-center text-muted-foreground animate-pulse">Loading officials...</div>
          ) : officialsData.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              No officials assigned yet. Edit the grant to assign officials.
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {officialsData.map((off, i) => (
                <div key={`${off.role}-${i}`} className="rounded-lg border p-4 space-y-3" data-testid={`official-card-${off.role}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-black text-[#D4E100] flex items-center justify-center font-bold shrink-0">
                        {off.user?.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-semibold">{off.user?.name || 'Unassigned'}</p>
                        <Badge variant="outline">{roleLabel(off.role)}</Badge>
                      </div>
                    </div>
                    <div className="text-sm space-y-0.5">
                      {off.user?.email && (
                        <a href={`mailto:${off.user.email}`} className="text-primary hover:underline flex items-center gap-1.5">
                          <Mail className="h-3 w-3" /> {off.user.email}
                        </a>
                      )}
                      {off.user?.phone && (
                        <a href={`tel:${off.user.phone}`} className="text-muted-foreground flex items-center gap-1.5">
                          <Phone className="h-3 w-3" /> {off.user.phone}
                        </a>
                      )}
                    </div>
                  </div>

                  {off.responsibilities && (
                    <p className="text-sm text-muted-foreground border-l-2 border-[#D4E100] pl-3">{off.responsibilities}</p>
                  )}

                  {/* Performance Dashboard */}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Performance Dashboard</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      <div className="rounded-md bg-muted/40 p-2 text-center">
                        <p className="text-lg font-bold">{off.performance.deliverables_total}</p>
                        <p className="text-[11px] text-muted-foreground">Deliverables</p>
                      </div>
                      <div className="rounded-md bg-emerald-50 p-2 text-center">
                        <p className="text-lg font-bold text-emerald-600">{off.performance.completed}</p>
                        <p className="text-[11px] text-muted-foreground">Completed</p>
                      </div>
                      <div className="rounded-md bg-blue-50 p-2 text-center">
                        <p className="text-lg font-bold text-blue-600">{off.performance.in_progress}</p>
                        <p className="text-[11px] text-muted-foreground">In Progress</p>
                      </div>
                      <div className="rounded-md bg-rose-50 p-2 text-center">
                        <p className="text-lg font-bold text-rose-600">{off.performance.overdue}</p>
                        <p className="text-[11px] text-muted-foreground">Overdue</p>
                      </div>
                      <div className="rounded-md bg-muted/40 p-2 text-center col-span-2 sm:col-span-1">
                        <div className="flex items-center justify-center gap-1">
                          <p className="text-lg font-bold">{off.performance.avg_percentage}%</p>
                        </div>
                        <p className="text-[11px] text-muted-foreground">Avg Complete</p>
                      </div>
                    </div>
                  </div>

                  {/* Current Projects */}
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Current Projects</p>
                    <div className="flex flex-wrap gap-1.5">
                      {off.current_grants.map(g => (
                        <Badge key={g.id} className="bg-black text-[#D4E100]">{g.name}</Badge>
                      ))}
                      {off.current_projects.map(p => (
                        <Badge key={p.id} variant="outline">{p.name}</Badge>
                      ))}
                      {off.current_grants.length === 0 && off.current_projects.length === 0 && (
                        <span className="text-xs text-muted-foreground italic">No active grants or projects</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfficialsDialog(false)} data-testid="officials-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GrantsPage;
